'use strict';

const { getDependencyClass } = require('../../../lib/dependencies.js');
getDependencyClass('phoenix-page').then((pageClass) => {
    class PhoenixCarouselStrip extends pageClass.PhoenixElement {
        get observedBroadcastedEvents() {
            const baseEvents = ['phoenix:carousel:activeSlide', 'phoenix:breakpoint:changed'];
            // This is a case needed for hashed TabPanels. In the case that the tab that contains the
            // CollectionStrips is initially hidden.
            if (!this.config?.isInPanel) {
                return baseEvents;
            }
            return [...baseEvents, 'phoenix:tab:active'];
        }

        get carousel() {
            return this._carousel || (this._carousel = this.closest('phoenix-carousel'));
        }

        get contentWidth() {
            if (!this._contentWidth) {
                this._contentWidth = this.carouselContent.scrollWidth;
                // Removing Negative margin from collection strip layout
                const { marginLeft, marginRight } = window.getComputedStyle(this.carouselContent);
                this._contentWidth += isNaN(parseInt(marginLeft)) ? 0 : parseInt(marginLeft);
                this._contentWidth += isNaN(parseInt(marginRight)) ? 0 : parseInt(marginRight);
            }

            return this._contentWidth;
        }

        set hidePreviousButton(val) {
            if (!this.previousButton) {
                return;
            }

            this.previousButton.setAttribute('aria-hidden', val);
            this.previousButton.hidden = val;
            this.previousButton.classList.toggle('is-visible', val);
        }

        set hideNextButton(val) {
            if (!this.nextButton) {
                return;
            }
            this.nextButton.setAttribute('aria-hidden', val);
            this.nextButton.hidden = val;
            this.nextButton.classList.toggle('is-visible', val);
        }

        connectedCallback() {
            super.connectedCallback();
            this.waitForElement('phoenix-carousel-slide', { untilDomReady: true }).then((slide) => {
                this.container = this.querySelector('.m-collection-strip--carousel-container');
                this.carouselContent = this.querySelector('.m-collection-strip--carousel-content');
                this.nextButton = this.querySelector('[event-name="phoenix:carousel:next"]');
                this.previousButton = this.querySelector('[event-name="phoenix:carousel:previous"]');
                this._checkIfScrollable();
            });
        }

        assignSlidePositions() {
            let pos = 0;
            this.carousel.slides.forEach((slide) => {
                slide.setAttribute('data-pos', pos);
                pos += slide.offsetWidth;
            });
        }

        broadcastedEventCallback(name, args = {}) {
            const currentSlideIndex = args.detail && args.detail.value;

            switch (name) {
                case 'phoenix:breakpoint:changed':
                    // force content width reset
                    this._contentWidth = false;
                    this._checkIfScrollable();
                    break;
                case 'phoenix:carousel:activeSlide':
                    if (args.target === this.carousel) {
                        let slide = this.carousel.slides[currentSlideIndex];
                        this.carouselContent.scrollLeft = slide.getAttribute('data-pos');
                    }
                    break;
                // This is a case needed for hashed TabPanels. In the case that the tab that contains the
                // CollectionStrips is initially hidden.
                case 'phoenix:tab:active':
                    if (this.config?.isInPanel && this.container) {
                        const activePanelId = args.detail?.panelId;
                        const panelId = this.closest('phoenix-tab-panel')?.id;
                        if (panelId !== activePanelId) {
                            break;
                        }
                        // waitForElement because it has to render on page before we can calculate widths.
                        this.waitForElement('.m-collection-strip--carousel-container', {
                            untilDomReady: true,
                        }).then(() => {
                            this._contentWidth = false;
                            this._checkIfScrollable();
                        });
                    }
                    break;
            }
        }

        _checkIfScrollable() {
            this.containerWidth = this.container.clientWidth;
            this.contentOverflow = this.contentWidth - this.containerWidth;
            this.isScrollable = this.containerWidth < this.contentWidth;

            if (this.isScrollable) {
                this._addListener();
                this.assignSlidePositions();
                this._checkNavButtonStatus();
            } else {
                this._removeListener();
                this.hidePreviousButton = true;
                this.hideNextButton = true;
            }
        }

        _checkNavButtonStatus() {
            const scrollLeft = this.carouselContent.scrollLeft;
            // Hide/ Show Next/Previous Button
            this.hidePreviousButton = scrollLeft === 0;
            this.hideNextButton = scrollLeft === this.contentOverflow;
        }

        _addListener() {
            this.carouselContent.addEventListener('scroll', this._checkNavButtonStatus.bind(this));
        }

        _removeListener() {
            this.carouselContent.removeEventListener('scroll', this._checkNavButtonStatus.bind(this));
        }

        disconnectedCallback() {
            super.disconnectedCallback();
            this._removeListener();
        }
    }
    window.customElements.define('phoenix-carousel-strip', PhoenixCarouselStrip);
});
